import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, InputGroup } from '@/common'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { EmailInputGroup } from '@/shared/components'
import { LoginFormData, loginFormSchema } from './LoginForm.schema'
import { LoginPayload } from '../hooks/useAuth'

export default function LoginForm({
  login,
  isLoggingIn
}: {
  login: (payload: LoginPayload) => void
  isLoggingIn: boolean
}) {
  const { t } = useTranslation()
  const [formState, setFormState] = useState<LoginFormData>({
    email: '',
    password: ''
  })
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string
    password?: string
  }>({})

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = evt.target
    setFormState(prev => ({
      ...prev,
      [name]: evt.target.value
    }))
    // Clear error when user starts typing
    if (name in fieldErrors) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const validateEmail = useCallback(
    (email: string) => {
      const result = loginFormSchema.shape.email.safeParse(email)
      if (!result.success) {
        setFieldErrors(prev => ({
          ...prev,
          email: result.error.issues[0].message
        }))
        return false
      }
      setFieldErrors(prev => ({ ...prev, email: undefined }))
      return true
    },
    [t]
  )

  const validatePassword = useCallback(
    (password: string) => {
      const result = loginFormSchema.shape.password.safeParse(password)
      if (!result.success) {
        setFieldErrors(prev => ({
          ...prev,
          password: result.error.issues[0].message
        }))
        return false
      }
      setFieldErrors(prev => ({ ...prev, password: undefined }))
      return true
    },
    [t]
  )

  const onSubmit = useCallback(
    async (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault()

      // Validate all fields before submit
      const isEmailValid = validateEmail(formState.email)
      const isPasswordValid = validatePassword(formState.password)

      if (!isEmailValid || !isPasswordValid) {
        return // Don't submit if validation fails
      }

      const result = loginFormSchema.safeParse(formState)
      if (!result.success) {
        return
      }

      try {
        await login(formState)
      } catch {
        toast.error(t('login.errors.general'))
      }
    },
    [formState, login, t, validateEmail, validatePassword]
  )

  return (
    <form onSubmit={onSubmit}>
      <div className="space-y-4 mb-8">
        <EmailInputGroup
          onChange={onChange}
          onBlur={evt => validateEmail(evt.target.value)}
          name="email"
          label={t('loginForm.fields.email.label')}
          error={fieldErrors.email ? t(fieldErrors.email) : undefined}
        />
        <InputGroup
          onChange={onChange}
          onBlur={evt => validatePassword(evt.target.value)}
          name="password"
          label={t('loginForm.fields.password.label')}
          type="password"
          error={fieldErrors.password ? t(fieldErrors.password) : undefined}
          hint={
            <Link className="text-green-600 underline" to="/forgot-password">
              {t('loginForm.fields.password.forgotPassword')}
            </Link>
          }
        />
      </div>
      <Button
        isLoading={isLoggingIn}
        disabled={isLoggingIn}
        className="px-4 py-2 rounded-lg w-full h-10 text-center"
        type="submit"
      >
        {t('loginForm.actions.login')}
      </Button>
    </form>
  )
}
